<template>
  <div v-if="order">
    <div class="border-b border-dark-b">
      <h2 class="text-xl md:text-2xl font-medium">Order Details</h2>
    </div>
    <div class="py-8">
      <div class="bg-white md:flex justify-between rounded-lg border border-dark-b">
        <div class="p-4 md:w-2/5">
          <p>
            <span class="font-semibold">Ordered On: </span>
            <span>{{ order.orderInformation.orderPlacedAt }}</span>
          </p>
          <p>
            <span class="font-semibold">Order Number: </span>
            <span>{{ order.orderInformation.orderReferenceNumber }}</span>
          </p>
          <p v-if="order.supportTickets[0]">
            <span class="font-semibold">Request Assistance# </span>
            <span>{{ order.supportTickets[0].ticketNumber }}</span>
          </p>
        </div>
        <div class="p-4 md:w-2/5">
          <p>
            <span class="font-semibold">Order Status: </span>
            <span>{{ order.orderInformation.status }}</span>
          </p>
          <p>
            <span class="font-semibold">Payment Method: </span>
            <span>{{ order.payment.paymentMethod }}</span>
          </p>
        </div>
        <div class="p-4 md:w-1/5 self-center">
          <button v-if="onlinePayment" @click="getInvoice" class="flex items-center">
            <i class="material-icons text-gray-550">get_app</i>
            <span class="text-blue-600 pl-2">Invoice</span>
          </button>
        </div>
      </div>
      <div class="flex flex-wrap bg-white p-4 justify-between mt-6 rounded-lg border border-dark-b">
        <div class="w-1/3">
          <h2 class="font-semibold">Shipping Address</h2>
          <p>{{ order.addresses.shippingFullName }}</p>
          <p v-html="order.addresses.shippingAddress.replace(/(?:\r\n|\r|\\n)/g, '<br />')"></p>
          <p>{{ order.addresses.shippingPhoneNumber }}</p>
        </div>
        <div class="w-1/3">
          <h2 class="font-semibold">Billing Address</h2>
          <p>{{ order.addresses.billingFullName }}</p>
          <p v-html="order.addresses.billingAddress.replace(/(?:\r\n|\r|\\n)/g, '<br />')"></p>
          <p>{{ order.addresses.billingPhoneNumber }}</p>
        </div>
        <div class="md:w-1/3">
          <h2 class="font-semibold">Order Summary</h2>
          <div>
            <div class="flex justify-between">
              <div>Subtotal:</div><div>{{ order.totals.orderTotal }}</div>
            </div>
            <div class="flex justify-between">
              <div>Tax:</div><div>{{ order.totals.taxTotal }}</div>
            </div>
            <div class="flex justify-between" v-if="order.additionalPurchases.hasPurchasedTesting">
              <div>Testing by Intertek:</div><div>$0.00</div>
            </div>
            <div class="flex justify-between" v-if="order.additionalPurchases.hasPurchasedInspection">
              <div>Inspection by Intertek:</div>
              <div>{{ order.totals.inspectionCharges }}</div>
            </div>
            <div class="flex justify-between" v-if="order.additionalPurchases.hasPurchasedInsurance">
              <div>Export Insurance:</div>
              <div>{{ order.totals.exportInsurance }}</div>
            </div>
            <div class="flex justify-between" v-if="order.additionalPurchases.hasSignedUpForDrs">
              <div class="w-2/3">Become verified with DRS by Dun & Bradstreet:</div>
              <div>{{ order.totals.drsSignUpCharges }}</div>
            </div>
            <div class="flex justify-between">
              <div>Platform Fee:</div>
              <div>{{ order.totals.platformCharges }}</div>
            </div>
            <div class="flex justify-between">
              <div class="font-bold mt-3">Grand Total:</div>
              <div class="font-bold mt-3">{{ order.totals.grandTotal }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="border-b border-dark-b pt-8">
        <h2 class="text-lg font-semibold">Products</h2>
      </div>
      <div
        v-for="(product, index) in order.orderDetails"
        :key="`order-product-${index}`"
         class="bg-white p-4 mt-6 rounded-lg border border-dark-b"
      >
        <div
          class="flex items-center"
        >
          <div class="w-1/4 mr-4">
            <img :src="product.images[0]" class="w-32">
          </div>
          <div class="text-sm">
            <router-link
              :to="`/products/${product.id}`"
              class="text-blue-600"
            >
              {{ product.name }}
            </router-link>
            <p>Price: {{ product.total }}</p>
            <p>Quantity: {{ product.quantity }}</p>
            <p>INCO Terms: {{ product.incoTerms }}</p>
            <router-link
              :to="`/suppliers/${product.supplier.id}`"
              class="text-blue"
            >
              {{ product.supplier.name }}
            </router-link>
          </div>
        </div>  
        <h3 class="font-semibold my-6">Item Status Timeline</h3>
        <div class="flex justify-between flex-wrap md:px-8">
          <div
            v-for="(status, ind) in product.orderDetailsStatusHistory"
            :key="`orderDetailsStatusHistory-${index}-${ind}`"
            class="flex items-center"
          >
            <i class="material-icons text-theme">check_circle</i>
            <div class="leading-1 pl-4">
              <p>{{ status.status }}</p>
              <p class="text-xs">{{ status.date }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'

export default {
  name: 'OrderInfo',
  data() {
    return {
      order: null,
    }
  },
  async created() {
    this.order = await this.fetchOrderInfo(this.$route.params.id)
    if (!this.order.result) {
      this.$notify({
        group: 'buyHive',
        type: 'error',
        text: this.order.message,
        duration: 7000
      })
      this.$router.push({ name: 'My Orders' })
    }
  },
  computed: {
    onlinePayment() {
      return this.order.payment.paymentMethod === "CC" || this.order.payment.paymentMethod === "ACH"
    }
  },
  methods: {
    ...mapActions ([
      'fetchOrderInfo',
      'fetchOrderInvoice'
    ]),
    async getInvoice() {
      const invoice = await this.fetchOrderInvoice(this.$route.params.id)
      const url = window.URL.createObjectURL(invoice)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'invoice.pdf')
      document.body.appendChild(link)
      link.click()
      link.remove()
    }
  }
}
</script>